import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Steps, Button, message, Modal, Spin } from 'antd';
import { LoadingOutlined, CheckCircleFilled, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useBreadcrumbs } from '../context/BreadcrumbContext';
import { useAuthOrg } from '../context/AuthOrgContext';
import { useAdAccounts } from '../hooks/useAdAccounts';
import { CampaignProvider, useCampaign } from '../context/CampaignContext';
import { CampaignDetailsPanel } from '../components/campaign/CampaignDetailsPanel';
import { AdSetDetailsPanel } from '../components/campaign/AdSetDetailsPanel';
import { AdCreativePanel } from '../components/campaign/AdCreativePanel';
import { AdPreview } from '../components/campaign/AdPreview';
import { EstimatedReach } from '../components/campaign/EstimatedReach';
import { buildTargetingSpec, buildCampaignPayload, buildCampaignEditPayload, supportsAdvCustomAudienceObjective } from '../utils/campaignUtils';
import coreAPIService from '../apiServices/coreAPIService';
import { CTW_API_ENDPOINT } from '../utils';
import { handleApiError } from '../utils/errorHandler';
import { CampaignCreationResponse } from '../interfaces/apiTypes';
import { validateCampaignDetails, validateAdSetDetails, validateAdCreative } from '../utils/validationUtils';
import { useAudiences } from '../hooks/useAudiences';
import { useTargeting } from '../hooks/useTargeting';
import './MetaCampaignCreation.css';
import CommonLayout from '../common/LayoutBlock';
import dayjs from 'dayjs'
import { hasRolePermission } from '../utils/roles';

const { Step } = Steps;

const normalizeApiBoolean = (value: any): boolean => {
    return value === true || value === 1 || value === '1' || String(value).toLowerCase() === 'true';
};

const normalizeBeneficiaryName = (value: any): string | null => {
    if (!value) return null;
    if (typeof value === 'string') return value;
    return value.name || value.beneficiary || value.beneficiaryName || null;
};

const CampaignCreationContent: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { state, dispatch } = useCampaign();
    const { currentOrg, user, availableOrgs } = useAuthOrg();
    const { setBreadcrumbs } = useBreadcrumbs();
    const [metaAdsetId, setMetaAdsetId] = useState(null)
    // Local state for loading
    const [orgLoading, setOrgLoading] = useState(false);

    // Ad Accounts Hook
    const {
        adAccounts,
        selectedAdAccountId,
        adAccountLoading,
        handleAdAccountChange,
        fbPages,
        selectedFbPageId,
        pagesLoading,
        handleFbPageChange,
        selectedWaba,
        handleWabaChange,
        availableOrgs: orgs,
        isMilestoneCheckPassed,
        checkingMilestones,
        isMilestoneCheckErrorMsg,
        wabaList
    } = useAdAccounts(state.orgId || currentOrg?.orgId);

    // --- Lifted Hooks ---
    const {
        customItems,
        handleCustomSearch,
        handleCustomSelect,
        customAdSet
    } = useAudiences(state.selectedAdAccountId, state.orgId);

    const {
        locationOptions,
        searchLocations,
        isSearchingLocations,
        locationSearch,
        setLocationSearch,
        targetingOptions,
        searchInterests,
        isSearchingInterests,
        targetingSearch,
        setTargetingSearch,
        fetchDeliveryEstimate,
        estimatedReach: targetingEstimatedReach,
        loading: estimateLoading
    } = useTargeting(state.selectedAdAccountId, state.selectLocations, state.excludedLocations, state.selectedTargeting);


    // Sync Ad Account hook state with Context
    useEffect(() => {
        if (selectedAdAccountId !== state.selectedAdAccountId) {
            dispatch({ type: 'SET_FIELD', field: 'selectedAdAccountId', value: selectedAdAccountId });
        }
    }, [selectedAdAccountId]);

    useEffect(() => {
        if (selectedFbPageId !== state.selectedFbPageId) {
            dispatch({ type: 'SET_FIELD', field: 'selectedFbPageId', value: selectedFbPageId });
        }
    }, [selectedFbPageId]);

    useEffect(() => {
        if (selectedWaba !== state.selectedWaba) {
            dispatch({ type: 'SET_FIELD', field: 'selectedWaba', value: selectedWaba });
        }
    }, [selectedWaba]);

    // Set initial Org ID
    useEffect(() => {
        if (!state.orgId && currentOrg?.orgId) {
            dispatch({ type: 'SET_FIELD', field: 'orgId', value: currentOrg.orgId });
        }
    }, [currentOrg]);

    // Load campaign data from URL params or Location State (for pre-filling)
    useEffect(() => {
        const initEditMode = async () => {
            const params = new URLSearchParams(window.location.search);
            const editUuid = params.get('edit');
            const cloneUuid = params.get('clone');
            const isCloneMode = !!cloneUuid || (location.state as any)?.isCloneMode;
            const locationState = location.state as { campaignData: any; isEditMode: boolean };

            let campaignData = locationState?.campaignData;


            if ((editUuid || cloneUuid) && !campaignData) {
                // Fallback: Fetch all campaigns and find the one with the UUID
                setOrgLoading(true);
                try {
                    const res = await coreAPIService.get<any>(CTW_API_ENDPOINT, 'v2/org/times-panel/campaigns/' + (state.orgId || currentOrg?.orgId));
                    if (res.meta && res.meta.code === 200) {
                        campaignData = res.data.find((c: any) => c.id === (editUuid || cloneUuid));
                    }
                } catch (error) {
                    console.error("Failed to fetch campaign for edit/clone", error);
                } finally {
                    setOrgLoading(false);
                }
            }

            if (campaignData) {
                // console.log(campaignData);

                // Populate State
                try {
                    const targeting = typeof campaignData.targeting === 'string'
                        ? JSON.parse(campaignData.targeting)
                        : campaignData.targeting;

                    // console.log(targeting);


                    setMetaAdsetId(isCloneMode ? null : campaignData.metaAdsetId)
                    const advantagePlus = normalizeApiBoolean(campaignData?.advantagePlus ?? campaignData?.advantage_plus);
                    const advCustomAudience = normalizeApiBoolean(
                        campaignData?.advCustomAudience ??
                        campaignData?.adv_custom_audience ??
                        targeting?.adv_custom_audience
                    );
                    const beneficiary = normalizeBeneficiaryName(
                        campaignData?.beneficiary ??
                        campaignData?.beneficiaryName ??
                        targeting?.beneficiary
                    );
                    const selectedObjective = campaignData.objective;
                    const canUseAdvCustomAudience = supportsAdvCustomAudienceObjective(selectedObjective);

                    dispatch({
                        type: 'SET_MULTIPLE_FIELDS',
                        payload: {
                            isEditing: !isCloneMode,
                            campaignName: isCloneMode ? null : campaignData.campaignName,
                            selectedAdAccountId: campaignData.adAccountId,
                            selectedWaba: campaignData.wabaNumber,
                            selectedFbPageId: campaignData.fbPage,
                            orgId: campaignData.orgId,
                            adSetName: isCloneMode ? `Copy of ${campaignData.adsetName}` : campaignData.adsetName,
                            budgetType: campaignData.dailyBudget ? "DAILY" : "LIFETIME",
                            lifetimeBudget: campaignData.lifetimeBudget,
                            dailyBudget: campaignData.dailyBudget,
                            startDate: isCloneMode ? dayjs() : dayjs(campaignData.startTime),
                            endDate: campaignData.endTime ? dayjs(campaignData.endTime) : null,
                            primaryText: campaignData.adCreativeMessage,
                            headline: campaignData.adCreativeLinkName,
                            descriptionText: campaignData.adCreativeLinkMessage,
                            mediaUrl: campaignData.adCreativeImageLink,

                            // Targeting — use || [] on every spread to guard against null/undefined fields
                            ageRange: [targeting?.age_min ?? 18, targeting?.age_max ?? 65],
                            selectLocations: [
                                ...(targeting?.geo_locations?.cities ?? []),
                                ...(targeting?.geo_locations?.regions ?? []),
                                ...(targeting?.geo_locations?.countries ? targeting?.geo_locations?.countries.map((country: string) => ({ key: country, name: country, type: "country", country_name: country })) : []),
                                ...(targeting?.geo_locations?.neighborhoods ?? []),
                            ],
                            excludedLocations: [
                                ...(targeting?.excluded_geo_locations?.cities ?? []),
                                ...(targeting?.excluded_geo_locations?.regions ?? []),
                                ...(targeting?.excluded_geo_locations?.countries ? targeting?.excluded_geo_locations?.countries.map((country: string) => ({ key: country, name: country, type: "country", country_name: country })) : []),
                                ...(targeting?.excluded_geo_locations?.neighborhoods ?? []),
                            ],
                            selectedTargeting: [
                                ...(targeting?.interests ?? []).map((i: any) => ({ id: i.id, name: i.name, type: 'INTEREST' })),
                                ...(targeting?.behaviors ?? []).map((b: any) => ({ id: b.id, name: b.name, type: 'BEHAVIOR' })),
                                ...(targeting?.demographics ?? []).map((d: any) => ({ id: d.id, name: d.name, type: 'DEMOGRAPHIC' })),
                            ],
                            customAdSetId: targeting?.custom_audiences?.[0]?.id || null,
                            useCustomAudiences: (targeting?.custom_audiences?.length ?? 0) > 0,
                            selectedObjective,
                            prefilledMessage: campaignData.prefilledMessage,
                            welcomeMessage: campaignData.welcomeMessage,
                            adsetSchedule: campaignData.adsetScheduling ? JSON.parse(campaignData.adsetScheduling) : [],
                            currentStep: isCloneMode ? 0 : 0,
                            selectedGender: campaignData.gender || "all",
                            advCustomAudience: advantagePlus || !canUseAdvCustomAudience ? false : advCustomAudience,
                            setBeneficiary: !!beneficiary,
                            beneficiary,
                            ads: (campaignData.ads ?? []).map((item: any) => ({
                                adName: isCloneMode ? '' : item.adName,
                                primaryText: item.adCreativeMessage,
                                descriptionText: item.adCreativeLinkMessage,
                                headline: item.adCreativeLinkName,
                                mediaType: item.videoId ? 'video' : 'image',
                                mediaUrl: item.adCreativeImageLink || '',
                                videoId: item.videoId || '',
                                thumbnailUrl: item.thumbnailImageUrl || item.adVideoThumbnailUrl || '',
                                imageSource: item.imageSource || item.image_source || 's3',
                                metaImageHash: item.metaImageHash || item.meta_image_hash || '',
                                metaThumbnailImageHash: item.metaThumbnailImageHash || item.meta_thumbnail_image_hash || '',
                                prefilledMessage: item.prefilledMessage,
                                welcomeMessage: item.welcomeMessage,
                            })),
                            customAdSetName: targeting?.custom_audiences?.[0]?.name || null,
                            advantagePlus,
                        }

                    });
                    // If we have ad account ID, trigger side effects to load pages/wabas
                    // Pass `true` so existing FB page & WABA from campaign data are preserved
                    if (campaignData.adAccountId) {
                        handleAdAccountChange(campaignData.adAccountId, true);
                    }

                } catch (e) {
                    console.error("Error parsing campaign data for edit/clone", e);
                }
            }
        };

        initEditMode();
    }, []);


    // Option 1: Use debouncing
    useEffect(() => {
        if (!state.campaignName || state.adSetName) return;

        const timer = setTimeout(() => {
            const dateStr = dayjs().format('DDMMYY');
            dispatch({ type: 'SET_FIELD', field: 'adSetName', value: `${state.campaignName}_adset_${dateStr}` });

        }, 500); // Wait 500ms after user stops typing

        return () => clearTimeout(timer);
    }, [state.campaignName]);

    // Fetch Delivery Estimate when targeting changes
    useEffect(() => {
        fetchDeliveryEstimate(
            state.selectLocations,
            state.excludedLocations,
            state.selectedTargeting,
            state.ageRange,
            state.useCustomAudiences ? [{ id: state.customAdSetId || "", name: state.customAdSetName || "" }] : undefined
        );
    }, [
        state.selectLocations,
        state.excludedLocations,
        state.selectedTargeting,
        state.ageRange,
        state.useCustomAudiences,
        state.customAdSetId,
        state.customAdSetName
    ]);

    // Update Context with Estimate
    useEffect(() => {
        if (targetingEstimatedReach) {
            dispatch({ type: 'SET_FIELD', field: 'estimatedRange', value: targetingEstimatedReach });
        }
    }, [targetingEstimatedReach]);

    // Breadcrumbs
    useEffect(() => {
        setBreadcrumbs([
            {
                title: <div className="text-lg cursor-pointer" onClick={() => navigate('/campaign-management')}>Campaign Management</div>,
            },
            {
                title: <div className="text-lg cursor-pointer">Campaign Creation</div>,
            },
        ]);
    }, [setBreadcrumbs, navigate]);

    const handleNext = () => {
        // isMilestoneCheckPassed
        let isValid = false;
        if (state.currentStep === 0) {
            isValid = validateCampaignDetails(state.campaignName, state.selectedAdAccountId, state.selectedFbPageId, state.selectedWaba, isMilestoneCheckPassed);
        } else if (state.currentStep === 1) {
            isValid = validateAdSetDetails(
                state.adSetName,
                false,
                state.useCustomAudiences,
                null,
                state.customAdSetName,
                state.selectLocations,
                state.excludedLocations,
                state.selectedGender,
                state.ageRange,
                state.selectedPlatforms,
                state.dailyBudget || state.lifetimeBudget,
                state.startDate,
                state.setBeneficiary,
                state.beneficiary
            );
        } else if (state.currentStep === 2) {
            // Ad Creative validation
            isValid = validateAdCreative(state.ads);
        }

        if (isValid) {
            dispatch({ type: 'SET_FIELD', field: 'currentStep', value: state.currentStep + 1 });
        }
    };

    const handlePrev = () => {
        dispatch({ type: 'SET_FIELD', field: 'currentStep', value: state.currentStep - 1 });
    };

    const handleSubmit = async () => {
        dispatch({ type: 'SET_FIELD', field: 'isSubmitting', value: true });
        const canUseAdvCustomAudience = supportsAdvCustomAudienceObjective(state.selectedObjective);
        const advCustomAudience = canUseAdvCustomAudience && state.advCustomAudience;
        if (metaAdsetId) {
            try {
                const payload = buildCampaignEditPayload(
                    state.campaignName,
                    state.endDate,
                    state.dailyBudget,
                    buildTargetingSpec(
                        state.useCustomAudiences,
                        state.customAdSetId ? [{ id: state.customAdSetId }] : undefined,
                        state.selectLocations,
                        state.excludedLocations,
                        state.ageRange,
                        state.selectedTargeting,
                        state.selectedPlatforms
                    ),

                    state.selectedAdAccountId,
                    state.selectedWaba!,
                    state.selectedFbPageId,
                    state.orgId,
                    "LOWEST_COST_WITHOUT_CAP", // bidStrategy
                    "CONVERSATIONS", // optimizationGoal
                    state.adsetSchedule,
                    state.budgetType,
                    state.lifetimeBudget,
                    state.advantagePlus,
                    advCustomAudience,
                    state.setBeneficiary ? state.beneficiary : null,
                    state.instagramAccountId,
                );

                const response = await coreAPIService.post<CampaignCreationResponse>(
                    CTW_API_ENDPOINT,
                    'v2/campaigns/ctwa/update/ctwa',
                    { ...payload, "adsetId": metaAdsetId }
                );

                if (response.meta.code == 200) {
                    // message.success('Campaign updated successfully!');
                    Modal.success({
                        title: 'Campaign Updated',
                        content: 'Your Campaign updation request received and being processed. Check status on Dashboard.',
                        onOk: () => navigate('/campaign-management'),
                    });
                } else {
                    handleApiError(response, 'Campaign Creation', 'Failed to create campaign');
                }
            } catch (error) {
                handleApiError(error, 'Campaign Creation', 'An unexpected error occurred');
            } finally {
                dispatch({ type: 'SET_FIELD', field: 'isSubmitting', value: false });
            }
        } else {

            try {
                const payload = buildCampaignPayload(
                    state.campaignName,
                    state.startDate,
                    state.endDate,
                    state.selectedObjective,
                    state.adSetName,
                    state.dailyBudget,
                    state.useCustomAudiences,
                    state.customAdSetId,
                    buildTargetingSpec(
                        state.useCustomAudiences,
                        state.customAdSetId ? [{ id: state.customAdSetId }] : undefined,
                        state.selectLocations,
                        state.excludedLocations,
                        state.ageRange,
                        state.selectedTargeting,
                        state.selectedPlatforms
                    ),
                    state.ads,
                    state.selectedAdAccountId,
                    state.selectedWaba!,
                    state.selectedFbPageId,
                    state.orgId,
                    undefined, // spendCap
                    state.specialAdCategories,
                    "LOWEST_COST_WITHOUT_CAP", // bidStrategy
                    "CONVERSATIONS", // optimizationGoal
                    state.adsetSchedule,
                    state.budgetType,
                    state.lifetimeBudget,
                    state.advantagePlus,
                    advCustomAudience,
                    state.setBeneficiary ? state.beneficiary : null,
                    state.instagramAccountId,
                );

                const response = await coreAPIService.post<CampaignCreationResponse>(
                    CTW_API_ENDPOINT,
                    'v2/campaigns/ctwa/create',
                    payload
                );

                if (response.meta.code == 200) {
                    // message.success('Campaign created successfully!');
                    Modal.success({
                        title: 'Campaign Created',
                        content: 'Your Campaign creation request received and being processed. Check status on Dashboard.',
                        onOk: () => navigate('/campaign-management'),
                    });
                } else {
                    handleApiError(response, 'Campaign Creation', 'Failed to create campaign');
                }
            } catch (error) {
                handleApiError(error, 'Campaign Creation', 'An unexpected error occurred');
            } finally {
                dispatch({ type: 'SET_FIELD', field: 'isSubmitting', value: false });
            }
        }
    };

    const steps = [
        {
            title: 'Campaign Details',
            description: 'Objective & Setup',
            content: <CampaignDetailsPanel
                adAccounts={adAccounts}
                adAccountLoading={adAccountLoading}
                handleAdAccountChange={handleAdAccountChange}
                orgs={orgs}
                orgLoading={orgLoading}
                handleOrgChange={(val) => dispatch({ type: 'SET_FIELD', field: 'orgId', value: val })}
                fbPages={fbPages}
                pagesLoading={pagesLoading}
                handleFbPageChange={handleFbPageChange}
                wabaList={wabaList}
                handleWabaChange={handleWabaChange}
                isMilestoneCheckPassed={isMilestoneCheckPassed}
                checkingMilestones={checkingMilestones}
                isMilestoneCheckErrorMsg={isMilestoneCheckErrorMsg}
                isAdmin={hasRolePermission(user?.userRole, 'accessSetup')}
            />,
        },
        {
            title: 'Audience & Budget',
            description: 'Targeting & Schedule',
            content: <AdSetDetailsPanel
                customItems={customItems}
                handleCustomSearch={handleCustomSearch}
                handleCustomSelect={handleCustomSelect}
                customAdSet={customAdSet}
                locationOptions={locationOptions}
                searchLocations={searchLocations}
                isSearchingLocations={isSearchingLocations}
                locationSearch={locationSearch}
                setLocationSearch={setLocationSearch}
                targetingOptions={targetingOptions}
                searchInterests={searchInterests}
                isSearchingInterests={isSearchingInterests}
                targetingSearch={targetingSearch}
                setTargetingSearch={setTargetingSearch}
                fetchDeliveryEstimate={fetchDeliveryEstimate}
            />,
        },
        {
            title: 'Ad Creative',
            description: 'Media & Text',
            content: <AdCreativePanel />,
        },
    ];

    return (
        <div className='py-2 px-10 flex flex-col md:flex-row'>
            <section className="w-full">
                <div className="bg-white shadow-md rounded-lg p-6">
                    <div className="mb-6 md:mb-8">
                        <h1 className="text-xl md:text-2xl font-bold text-gray-800 mb-2">Create New Campaign</h1>
                        <p className="text-sm md:text-base text-gray-500">Follow the steps below to set up your Meta campaign</p>
                    </div>

                    <Steps current={state.currentStep} className="custom-steps mb-2 bordered px-6">
                        {steps.map((item) => (
                            <Step key={item.title} title={item.title} description={item.description} />
                        ))}
                    </Steps>

                    <div className="steps-content mt-6 md:mt-8 min-h-[300px] md:min-h-[400px]">
                        {steps[state.currentStep].content}
                    </div>

                    <div className="steps-action mt-6 md:mt-8 flex flex-col sm:flex-row justify-between items-stretch sm:items-center border-t pt-4 md:pt-6 gap-4">
                        <div className="order-2 sm:order-1">
                            {state.currentStep > 0 && (
                                <Button
                                    onClick={handlePrev}
                                    size="large"
                                    icon={<LeftOutlined />}
                                    className="w-full sm:w-auto bg-[#1F344C] text-white"
                                >
                                    Previous
                                </Button>
                            )}
                        </div>
                        <div className="flex items-center gap-3 md:gap-4 order-1 sm:order-2">
                            {state.currentStep < steps.length - 1 && (
                                <Button
                                    // type="primary"
                                    onClick={handleNext}
                                    size="large"
                                    className="flex-1 sm:flex-none bg-[#1F344C] text-white"
                                >
                                    Next <RightOutlined />
                                </Button>
                            )}
                            {state.currentStep === steps.length - 1 && (
                                <Button
                                    // type="primary"
                                    onClick={handleSubmit}
                                    size="large"
                                    loading={state.isSubmitting}
                                    icon={state.isSubmitting ? <LoadingOutlined /> : <CheckCircleFilled />}
                                    className="flex-1 sm:flex-none bg-[#1F344C] text-white"
                                >
                                    Launch Campaign
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Ad Preview Section */}
            <div className="w-full min-w-[300px] md:w-auto flex-shrink-0 ml-0 md:ml-4 mt-4 md:mt-0">
                <AdPreview
                    primaryText={state.ads[state.currentAdIndex]?.primaryText || ''}
                    headline={state.ads[state.currentAdIndex]?.headline || ''}
                    descriptionText={state.ads[state.currentAdIndex]?.descriptionText || ''}
                    mediaUrl={state.ads[state.currentAdIndex]?.mediaUrl || ''}
                    facebookPage={fbPages.find(p => p.id === state.selectedFbPageId)?.name || ''}
                />
                <EstimatedReach estimatedReach={state.estimatedRange || null} loading={estimateLoading} />
            </div>

            {/* Loading Modal */}
            <Modal
                open={state.isSubmitting}
                closable={false}
                footer={null}
                centered
                maskClosable={false}
                width={400}
                className="px-4"
            >
                <div className="flex flex-col items-center justify-center p-6 md:p-8">
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
                    <p className="text-lg md:text-xl font-semibold mt-4 md:mt-6 text-gray-800">Creating Campaign...</p>
                    <p className="text-sm md:text-base text-gray-500 mt-2 text-center">
                        Please wait while we set up your campaign on Meta.
                    </p>
                </div>
            </Modal>
        </div>
    );
};

export const MetaCampaignCreation: React.FC = () => {
    return (
        <CampaignProvider>
            <CampaignCreationContent />
        </CampaignProvider>
    );
};

export default MetaCampaignCreation;
